import { useEffect, useRef, useCallback } from 'react';
import { Music } from 'lucide-react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { triggerDownload } from '@/utils/download';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

interface AudioPlayerProps {
  url: string;
  title?: string;
  fileName?: string;
  mimeType?: string;
  onClose: () => void;
  onDownload?: () => void;
  onProgress?: (progress: number) => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

export function AudioPlayer({
  url,
  title,
  fileName,
  mimeType,
  onClose,
  onDownload,
  onProgress,
  fileId,
  favoriteCachedData,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerRef = useRef<Plyr | null>(null);

  useEffect(() => {
    if (audioRef.current && !playerRef.current) {
      playerRef.current = new Plyr(audioRef.current, {
        controls: ['play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings'],
        settings: ['speed'],
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
        keyboard: { focused: true, global: true },
        tooltips: { controls: true, seek: true },
      });

      if (onProgress) {
        const handleTimeUpdate = () => {
          const player = playerRef.current;
          if (player && player.duration > 0) {
            onProgress((player.currentTime / player.duration) * 100);
          }
        };
        playerRef.current.on('timeupdate', handleTimeUpdate);
      }
    }

    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [onProgress]);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else {
      triggerDownload(url, fileName || title || 'audio');
    }
  }, [url, fileName, title, onDownload]);

  const getSourceType = (): string => {
    if (mimeType) return mimeType;
    const ext = url.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'mp3':
        return 'audio/mpeg';
      case 'wav':
        return 'audio/wav';
      case 'ogg':
        return 'audio/ogg';
      case 'm4a':
        return 'audio/mp4';
      case 'aac':
        return 'audio/aac';
      default:
        return 'audio/mpeg';
    }
  };

  return (
    <ViewerShell
      title={title || 'Audio'}
      type="audio"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={handleDownload}
      keyboardHint="Space to play/pause • Arrow keys to seek • M to mute"
    >
      <div className="flex items-center justify-center min-h-full p-4 sm:p-8">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-sm border border-[#f0d2f0]/30 overflow-hidden">
            <div className="bg-gradient-to-br from-[#f8e8f8] to-[#fcf4fc] p-6 sm:p-8 flex flex-col items-center">
              <div className="bg-[#8e4585]/10 p-5 rounded-2xl mb-4">
                <Music size={40} className="text-[#8e4585]" />
              </div>
              <h3 className="text-[#5a3d58] font-semibold text-base text-center truncate max-w-full">
                {title || 'Audio'}
              </h3>
            </div>
            <div className="p-4 sm:p-6">
              <audio ref={audioRef} className="plyr-react plyr w-full">
                <source src={url} type={getSourceType()} />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </div>
      </div>
    </ViewerShell>
  );
}
